import { create } from 'zustand'
import { type SearchMatch, searchPages } from '@/lib/pdf/text'
import { useDoc } from './documentStore'
import { useUi } from './uiStore'

interface SearchState {
  query: string
  caseSensitive: boolean
  wholeWord: boolean
  matches: SearchMatch[]
  active: number
  searching: boolean
  byPage: Map<string, SearchMatch[]>
  setQuery(q: string): void
  setOptions(o: Partial<Pick<SearchState, 'caseSensitive' | 'wholeWord'>>): void
  run(): void
  next(dir?: 1 | -1): void
  clear(): void
}

let controller: AbortController | null = null

export const useSearch = create<SearchState>()((set, get) => ({
  query: '',
  caseSensitive: false,
  wholeWord: false,
  matches: [],
  active: -1,
  searching: false,
  byPage: new Map(),
  setQuery: (query) => set({ query }),
  setOptions: (o) => {
    set(o)
    get().run()
  },
  run() {
    controller?.abort()
    const { query, caseSensitive, wholeWord } = get()
    if (!query.trim()) {
      set({ matches: [], active: -1, byPage: new Map(), searching: false })
      return
    }
    const ctrl = new AbortController()
    controller = ctrl
    set({ matches: [], active: -1, byPage: new Map(), searching: true })
    const pages = useDoc.getState().pages
    const current = useUi.getState().currentPage
    searchPages(pages, query, { caseSensitive, wholeWord }, ctrl.signal, (found) => {
      if (ctrl.signal.aborted) return
      const matches = [...get().matches, ...found]
      const byPage = new Map(get().byPage)
      byPage.set(found[0].pageId, found)
      let active = get().active
      // jump to the first match at or after the page the user is reading
      if (active < 0 && found[0].pageIndex >= current) {
        active = matches.indexOf(found[0])
        useUi.getState().goToPage(found[0].pageIndex, Math.max(0, found[0].rects[0]?.y - 80))
      }
      set({ matches, byPage, active })
    })
      .catch(() => {})
      .finally(() => {
        if (controller !== ctrl) return
        const s = get()
        if (s.active < 0 && s.matches.length) {
          set({ active: 0 })
          useUi.getState().goToPage(s.matches[0].pageIndex, Math.max(0, s.matches[0].rects[0]?.y - 80))
        }
        set({ searching: false })
      })
  },
  next(dir = 1) {
    const { matches, active } = get()
    if (!matches.length) return
    const i = (active + dir + matches.length) % matches.length
    set({ active: i })
    const m = matches[i]
    useUi.getState().goToPage(m.pageIndex, Math.max(0, (m.rects[0]?.y ?? 0) - 80))
  },
  clear() {
    controller?.abort()
    set({ query: '', matches: [], active: -1, byPage: new Map(), searching: false })
  },
}))
